import { getActivePreviewCompany, PREVIEW_ACTOR_ID } from './flag';
import { previewData } from './data';

// Preview Mode emulation of the communication journal (Phase 8.2, D-216 / migration 0142): the two
// RPCs' observable contract — newest page first, ascending inside a page, keyset paging back in
// time, server-set timestamps, idempotent create, the sanitized refusal codes. In-memory and
// append-only like the real table: nothing here edits or removes an entry.

type Row = Record<string, unknown>;
type Result<T> = { data: T; error: null | { message: string } };

interface PreviewPromise {
  readonly amount: string;
  readonly date: string;
  /** Canned verdict for the seeded demo entries; derived (weekends only, no payments) otherwise. */
  readonly canned?: { readonly verdict: 'KEPT' | 'UNRELIABLE'; readonly matched: string };
}

interface PreviewEntry {
  readonly entryId: string;
  readonly companyId: string;
  readonly customerId: string;
  readonly occurredAt: string;
  readonly note: string;
  readonly authorId: string;
  readonly authorName: string;
  readonly promise: PreviewPromise | null;
}

// Phase 8.3 (D-217) — a Preview approximation of 0143's derivation: the deadline skips weekends only
// (no calendar table here) and Preview has no payments to match, so a new promise is PENDING until
// its deadline passes and UNRELIABLE after. The two seeded promises carry canned verdicts so the demo
// shows both chips and a trust marker. UI verification only — the real rule lives in the database.
function isoDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function nextWeekdayAfter(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  do d.setUTCDate(d.getUTCDate() + 1); while (d.getUTCDay() === 0 || d.getUTCDay() === 6);
  return isoDay(d);
}
function toUnits(v: string): bigint {
  const [whole = '0', frac = ''] = v.split('.');
  return BigInt(whole) * 10_000n + BigInt(frac.padEnd(4, '0').slice(0, 4));
}
function fromUnits(u: bigint): string {
  return `${u / 10_000n}.${String(u % 10_000n).padStart(4, '0')}`;
}

function derive(p: PreviewPromise): Row {
  const deadline = nextWeekdayAfter(p.date);
  const verdict = p.canned?.verdict ?? (isoDay(new Date()) <= deadline ? 'PENDING' : 'UNRELIABLE');
  return { amount: fromUnits(toUnits(p.amount)), date: p.date, deadline, matched: p.canned?.matched ?? '0.0000', verdict };
}

const NOTE_MAX = 4000;
const entries: PreviewEntry[] = [];
const seeded = new Set<string>();
const replays = new Map<string, { hash: string; result: Row }>();

function text(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function customerExists(companyId: string, customerId: string): boolean {
  return ((previewData(companyId).tables['customers'] as Row[] | undefined) ?? []).some((c) => c['id'] === customerId);
}

/** Two demo entries per customer, written once, so the feed is never empty in the demo. */
function seed(companyId: string, customerId: string): void {
  const k = `${companyId}:${customerId}`;
  if (seeded.has(k)) return;
  seeded.add(k);
  entries.push(
    {
      entryId: `cl-${customerId}-1`, companyId, customerId, occurredAt: '2026-09-18T07:30:00.000Z',
      note: 'დავრეკე ბუღალტერიაში. ზედნადები მიიღეს, გადახდა შეთანხმებულია ხელმძღვანელთან.',
      authorId: 'preview-sales-manager', authorName: 'ნინო კაპანაძე',
      promise: { amount: '1500', date: '2026-09-19', canned: { verdict: 'KEPT', matched: '1500.0000' } },
    },
    {
      entryId: `cl-${customerId}-2`, companyId, customerId, occurredAt: '2026-09-22T11:05:00.000Z',
      note: 'შეხვედრა ოფისში. ვალის ნაწილს ამ თვის ბოლომდე ჩარიცხავენ, დანარჩენს — შემდეგ თვეში.',
      authorId: PREVIEW_ACTOR_ID, authorName: 'დემო ადმინისტრატორი',
      promise: null,
    },
  );
}

const byNewest = (a: PreviewEntry, b: PreviewEntry): number =>
  a.occurredAt === b.occurredAt ? (a.entryId < b.entryId ? 1 : -1) : a.occurredAt < b.occurredAt ? 1 : -1;

const split = (xs: readonly Row[]): Row => ({
  count: xs.length,
  amount: fromUnits(xs.reduce((s, x) => s + toUnits(text(x['amount'])), 0n)),
});

/** D-218 — fn_promise_summary over every Preview customer (Preview is always company scope). */
function promiseSummary(companyId: string): Row {
  const customers = (previewData(companyId).tables['customers'] as Row[] | undefined) ?? [];
  for (const c of customers) seed(companyId, text(c['id']));
  const all: Row[] = [];
  const rows = customers.flatMap((c): Row[] => {
    const vs = entries
      .filter((e) => e.companyId === companyId && e.customerId === c['id'] && e.promise !== null)
      .map((e) => derive(e.promise!));
    if (vs.length === 0) return [];
    all.push(...vs);
    const by = (v: string): Row[] => vs.filter((x) => x['verdict'] === v);
    const decided = by('KEPT').length + by('UNRELIABLE').length;
    return [{
      customerId: c['id'], customerName: c['name'] ?? null, taxId: text(c['tax_id']),
      ...split(vs), kept: split(by('KEPT')), unreliable: split(by('UNRELIABLE')), pending: split(by('PENDING')),
      trust: decided === 0 ? null : 5 * by('KEPT').length >= 4 * decided ? 'TRUSTED' : 'UNRELIABLE',
    }];
  });
  rows.sort((a, b) => (toUnits(text(b['amount'])) > toUnits(text(a['amount'])) ? 1 : -1));
  const byAll = (v: string): Row[] => all.filter((x) => x['verdict'] === v);
  return {
    ok: true, scope: 'company',
    totals: { ...split(all), kept: split(byAll('KEPT')), unreliable: split(byAll('UNRELIABLE')), pending: split(byAll('PENDING')) },
    customers: rows, truncated: false,
  };
}

/** fn_communication_log_list / _create and fn_promise_summary; `undefined` for any other function. */
export function previewCommunicationRpc(fn: string, args: Row): Result<unknown> | undefined {
  if (fn === 'fn_promise_summary') {
    return { data: promiseSummary(text(args['p_company']) || getActivePreviewCompany()), error: null };
  }
  if (fn !== 'fn_communication_log_list' && fn !== 'fn_communication_log_create') return undefined;
  const companyId = text(args['p_company']) || getActivePreviewCompany();
  const payload = (args['p_payload'] ?? {}) as Row;

  if (fn === 'fn_communication_log_list') {
    const filters = (payload['filters'] ?? {}) as Row;
    const page = (payload['page'] ?? {}) as Row;
    const customerId = text(filters['customerId']);
    if (!customerExists(companyId, customerId)) return { data: null, error: { message: 'COMMUNICATION_CUSTOMER_NOT_FOUND' } };
    seed(companyId, customerId);
    const limit = Math.min(Math.max(Number(page['limit'] ?? 50) || 50, 1), 200);
    const cursor = (page['cursor'] ?? null) as Row | null;
    const older = entries
      .filter((e) => e.companyId === companyId && e.customerId === customerId)
      .sort(byNewest)
      .filter((e) => cursor === null || byNewest(e, {
        ...e, occurredAt: text(cursor['occurredAt']), entryId: text(cursor['id']),
      }) > 0);
    const kept = older.slice(0, limit).reverse();
    const verdicts = entries
      .filter((e) => e.companyId === companyId && e.customerId === customerId && e.promise !== null)
      .map((e) => derive(e.promise!));
    const count = (v: string): number => verdicts.filter((x) => x['verdict'] === v).length;
    const decided = count('KEPT') + count('UNRELIABLE');
    return {
      data: {
        ok: true, customerId, scope: 'company', canWrite: true,
        items: kept.map(({ entryId, occurredAt, note, authorId, authorName, promise }) => ({
          entryId, occurredAt, note, authorId, authorName, promise: promise === null ? null : derive(promise),
        })),
        promiseSummary: {
          count: verdicts.length, kept: count('KEPT'), unreliable: count('UNRELIABLE'), pending: count('PENDING'),
          totalPromised: fromUnits(verdicts.reduce((s, x) => s + toUnits(text(x['amount'])), 0n)),
          trust: decided === 0 ? null : 5 * count('KEPT') >= 4 * decided ? 'TRUSTED' : 'UNRELIABLE',
        },
        page: { limit, returned: kept.length, hasMore: older.length > limit },
      },
      error: null,
    };
  }

  const customerId = text(payload['customerId']);
  const note = text(payload['note']).trim();
  const key = text(payload['idempotencyKey']).trim();
  if (key === '' || note === '' || note.length > NOTE_MAX) return { data: null, error: { message: 'COMMUNICATION_INVALID_PAYLOAD' } };
  const rawPromise = payload['promise'] as Row | null | undefined;
  let promise: PreviewPromise | null = null;
  if (rawPromise != null) {
    const amount = text(rawPromise['amount']).trim();
    const date = text(rawPromise['date']).trim();
    if (!/^\d{1,16}(\.\d{1,2})?$/.test(amount) || !/[1-9]/.test(amount) || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return { data: null, error: { message: 'COMMUNICATION_INVALID_PROMISE' } };
    }
    promise = { amount, date };
  }
  if (!customerExists(companyId, customerId)) return { data: null, error: { message: 'COMMUNICATION_CUSTOMER_NOT_FOUND' } };

  const opKey = `${companyId}:${key}`;
  const hash = `${customerId}:${note}:${promise === null ? '' : `${promise.amount}:${promise.date}`}`;
  const prior = replays.get(opKey);
  if (prior !== undefined) {
    return prior.hash === hash ? { data: prior.result, error: null } : { data: null, error: { message: 'IDEMPOTENCY_CONFLICT' } };
  }
  seed(companyId, customerId);
  const entry: PreviewEntry = {
    entryId: `cl-preview-${entries.length + 1}`, companyId, customerId, occurredAt: new Date().toISOString(), note,
    authorId: PREVIEW_ACTOR_ID, authorName: 'დემო ადმინისტრატორი', promise,
  };
  entries.push(entry);
  const result = { ok: true, entryId: entry.entryId, customerId, occurredAt: entry.occurredAt };
  replays.set(opKey, { hash, result });
  return { data: result, error: null };
}
